import React from 'react';
import { Avatar, List, ListItem, ListItemAvatar, ListItemText, Typography, Box, Stack, Button } from '@mui/material';
import { createTheme, ThemeProvider } from "@mui/material/styles";
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { IRoomState } from '../../../Core/State/IRoomState';
import styles from '../../../assets/css/components/RoomInfo.module.css';
import { IUserState } from '../../../Core/State/IUserState';
import { t } from '../../../i18n';
import SitOnTableButton from '../../Common/SitOnTableButton';

import bnbIcon from '/crypto/bnb.svg';
import busdIcon from '/crypto/busd.svg';
import daiIcon from '/crypto/dai.svg';
import dogeIcon from '/crypto/doge.svg';
import dotIcon from '/crypto/dot.svg';
import shibIcon from '/crypto/shib.svg';
import solIcon from '/crypto/sol.svg';
import solamaIcon from '/crypto/solama.svg';
import usdcIcon from '/crypto/usdc.svg';
import usdtIcon from '/crypto/usdt.svg';
import vlxIcon from '/crypto/vlx.svg';
import xmrIcon from '/crypto/xmr.svg';

// Table images
import cashTable from '/images/table/desktop-game-table.svg';
import aofTable from '/images/table/desktop-game-table-aof.svg';
import auraTable from '/images/table/desktop-game-table-aura.svg';
import bnbTable from '/images/table/desktop-game-table-bnb.svg';
import busdTable from '/images/table/desktop-game-table-busd.svg';
import daiTable from '/images/table/desktop-game-table-dai.svg';
import dogeTable from '/images/table/desktop-game-table-doge.svg';
import dotTable from '/images/table/desktop-game-table-dot.svg';
import kltvTable from '/images/table/desktop-game-table-kltv.svg';
import oplTable from '/images/table/desktop-game-table-opl.svg';
import shibTable from '/images/table/desktop-game-table-shib.svg';
import solTable from '/images/table/desktop-game-table-sol.svg';
import solamaTable from '/images/table/desktop-game-table-solama.svg';
import usdcTable from '/images/table/desktop-game-table-usdc.svg';
import usdtTable from '/images/table/desktop-game-table-usdt.svg';
import vlxTable from '/images/table/desktop-game-table-vlx.svg';
import xmrTable from '/images/table/desktop-game-table-xmr.svg';

interface RoomInformationProps {
    selectedRoom: IRoomState | null;
    joinRoom: (roomId: string, seatIndex?: number) => void;
    userState: IUserState;
}


const RoomInformation: React.FC<RoomInformationProps> = ({ selectedRoom, joinRoom, userState }) => {
    const darkTheme = createTheme({
        palette: {
            mode: "dark",
            primary: {
                main: "#F93751"
            }
        },
        typography: {
            fontFamily: 'Inter, serif',
        },
    });

    if (!selectedRoom) {
        return (
            <ThemeProvider theme={darkTheme}>
                <Box sx={{ display: 'flex', flexDirection: 'column', color: 'white', height: '100%', backgroundColor: '#2a2b32' }}>
                    <Stack sx={{ height: '100%' }}>
                        <Typography variant="h6" align="center" className={styles.boxTitle}>{t('RoomInformation:tableInfo')}</Typography>
                        <Typography align="center" className={styles.placeholder}>{t('RoomInformation:noTableSelected')}</Typography>
                    </Stack>
                </Box>
                {/* <Box sx={{ display: 'flex', flexDirection: 'column', color: 'white', height: '100px', backgroundColor: '#2a2b32', p: '20px', boxSizing: 'border-box' }}>
                    <Stack sx={{ height: '100%' }}>
                        <Typography style={{ display: 'flex', alignItems: 'center', marginBottom: '15px' }}><CircleIcon sx={{ width: '10px', height: '10px', color: '#3A3D50', mr: '10px' }}/>The table you clicked on</Typography>
                        <Typography style={{ display: 'flex', alignItems: 'center' }}><CircleIcon sx={{ width: '10px', height: '10px', color: '#2E6F6B', mr: '10px' }}/>The table you are currently sitting at</Typography>
                    </Stack>
                </Box> */}
            </ThemeProvider>
        );
    }

    const potAmount = selectedRoom.players.length > 0 ? selectedRoom.players[0].pot : 0;
    const playerSatCount = selectedRoom.playerSatCount ?? 0;
    const emptySeatCount = selectedRoom.maxPlayers - playerSatCount;

    // Count active players (not sitting out)
    const activePlayerCount = selectedRoom.players.filter(p => !p.isSitOut).length;

    const handleSeatClick = (seatIndex: number) => {
        if (selectedRoom) {
            // Pass the seatIndex with the join request
            joinRoom(selectedRoom.roomId, seatIndex);
        }
    };

    const emptySeats = Array.from({ length: emptySeatCount }, (_, index) => {
        const seatIndex = selectedRoom.players.length + index;
        return (
            <ListItem 
                alignItems="center" 
                sx={{ py: 0, px: 0, mb: '20px', cursor: 'pointer' }}
                onClick={() => handleSeatClick(seatIndex)}
                key={`empty-seat-${seatIndex}`}
            >
                <ListItemAvatar>
                    <SitOnTableButton size="small" />
                </ListItemAvatar>
                <ListItemText 
                    sx={{ margin: '9px' }}
                    primary={
                        <Typography variant="body2" component="span" sx={{ color: 'white', fontSize: '1rem', fontWeight: 500 }}>
                            {t('RoomInformation:clickToSitHere')}
                        </Typography>
                    }
                >
                </ListItemText>
            </ListItem>
        );
    });

    const formatCurrency = (value: number, currencyType: string) => {
        const symbol = currencyType === 'crypto' ? '' : '$';
        const amount = new Intl.NumberFormat('en-US', {
            minimumFractionDigits: value % 1 === 0 ? 0 : 2,
            maximumFractionDigits: 2
        }).format(value);
        return `${symbol}${amount}`;
    };

    const getCryptoIcon = (currency: string) => {
        switch(currency?.toLowerCase()) {
            case 'bnb':
                return bnbIcon;
            case 'busd':
                return busdIcon;
            case 'dai':
                return daiIcon;
            case 'doge':
                return dogeIcon;
            case 'dot':
                return dotIcon;
            case 'shib':
                return shibIcon;
            case 'sol':
                return solIcon;
            case 'solama':
                return solamaIcon;
            case 'usdc':
                return usdcIcon;
            case 'usdt':
                return usdtIcon;
            case 'vlx':
                return vlxIcon;
            case 'xmr':
                return xmrIcon;
            default:
                return undefined;
        }
    };

    const getTableImage = (selectedRoom: IRoomState) => {
        if (selectedRoom.currencyType == 'fiat') {
            if (selectedRoom.type == 'aof') {
                return aofTable;
            } else {
                return cashTable;
            }
        } else {
            switch(selectedRoom.currency?.toLowerCase()) {
                case 'aura':
                    return auraTable;
                case 'bnb':
                    return bnbTable;
                case 'busd':
                    return busdTable;
                case 'dai':
                    return daiTable;
                case 'doge':
                    return dogeTable;
                case 'dot':
                    return dotTable;
                case 'kltv':
                    return kltvTable;
                case 'opl':
                    return oplTable;
                case 'shib':
                    return shibTable;
                case 'sol':
                    return solTable;
                case 'solama':
                    return solamaTable;
                case 'usdc':
                    return usdcTable;
                case 'usdt':
                    return usdtTable;
                case 'vlx':
                    return vlxTable;
                case 'xmr':
                    return xmrTable;
                default:
                    return aofTable;
            }
        }
    };

    return (
        <ThemeProvider theme={darkTheme}>
            <Box sx={{ display: 'flex', flexDirection: 'column', color: 'white', height: '100%', backgroundColor: '#2a2b32' }}>
                <Stack sx={{ height: '100%' }}>
                    {/* <Typography variant="h6" align="center" className={styles.boxTitle}>Table Info</Typography> */}
                    {/* <Typography variant="subtitle1" align="center">{selectedRoom.name} - Pot: ${potAmount.toFixed(2)}</Typography> */}
                    <Box sx={{ py: '10px', px: '20px', background: '#222329', width: '100%', boxSizing: 'border-box' }}>
                        <img src={getTableImage(selectedRoom)} style={{ width: '100%' }}/>
                    </Box>
                    <List sx={{ 
                        width: '100%',
                        height: 'calc(100% - 137px)',
                        maxWidth: 256,
                        maxHeight: '100%',
                        boxSizing: 'border-box',
                        bgcolor: 'transparent',
                        overflow: 'auto',
                        padding: '1rem',
                        '& .MuiTypography-root': {
                            fontFamily: '"Inter", serif'
                        },
                        '&::-webkit-scrollbar': {
                            width: '0.4em'
                        },
                        '&::-webkit-scrollbar-track': {
                        },
                        '&::-webkit-scrollbar-thumb': {
                            backgroundColor: 'white',
                            borderRadius: '4px'
                        }
                    }}>
                        {selectedRoom.players.map((player) => (
                            <ListItem key={player.userId} alignItems="flex-start" sx={{ py: 0, px: 0 }}>
                                <ListItemAvatar sx={{ mt: 0, mb: '20px' }}>
                                    <Avatar 
                                        src={player.avatarUrl || undefined} 
                                        alt={player.username}
                                        sx={{ 
                                            width: 60,
                                            height: 60,
                                            bgcolor: player.avatarUrl ? 'transparent' : 'grey.300',
                                            color: player.avatarUrl ? 'inherit' : 'text.primary',
                                            margin: 0
                                        }}
                                    >
                                        {!player.avatarUrl && <HelpOutlineIcon sx={{ fontSize: 20 }} />}
                                    </Avatar>
                                </ListItemAvatar>
                                <ListItemText
                                    sx={{ margin: '9px' }}
                                    primary={
                                        <Typography variant="body2" component="span" sx={{ color: 'white', fontSize: '14px', fontWeight: 500 }}>
                                            {player.username}
                                        </Typography>
                                    }
                                    secondary={
                                        <Typography
                                            variant="caption"
                                            component="span"
                                            sx={{ color: 'white', fontSize: '12px', fontWeight: 400 }}
                                        >
                                            <br />
                                            <Stack direction="row" alignItems="center">
                                                {selectedRoom.currencyType === 'crypto' && (
                                                    <img src={getCryptoIcon(selectedRoom.currency)} alt={`${selectedRoom.currency} Icon`} style={{ marginRight: '4px' }} height={14} />
                                                )}
                                                {selectedRoom.ante && selectedRoom.ante > 0 ? 
                                                    `${formatCurrency(selectedRoom.ante, selectedRoom.currencyType)}` : 
                                                    `${formatCurrency(selectedRoom.smallBlind, selectedRoom.currencyType)}/${formatCurrency(selectedRoom.bigBlind, selectedRoom.currencyType)}`
                                                }
                                                <Typography sx={{ mx: 0.5 }}> - </Typography>
                                                {(activePlayerCount > 1 && !player.isSitOut) ? <span style={{ color: '#1be958', fontWeight: 'bold' }}> {t('RoomInformation:playing')}</span> : <span style={{ color: '#efc216', fontWeight: 'bold' }}> {t('RoomInformation:waiting')}</span>}
                                            </Stack>
                                        </Typography>
                                    }
                                />
                            </ListItem>
                        ))}
                        { emptySeats }
                    </List>
                    <Button
                        variant="contained"
                        style={{ color: 'white', textTransform: 'capitalize', fontWeight: 700, fontSize: '16px' }}
                        sx={{ mx: '1rem', mt: 'auto', mb: '1rem' }}
                        onClick={() => selectedRoom && joinRoom(selectedRoom.roomId)}
                    >
                        {t('RoomInformation:openTable')}
                    </Button>
                </Stack>
            </Box>
            {/* <Box sx={{ display: 'flex', flexDirection: 'column', color: 'white', height: '100px', backgroundColor: '#2a2b32', p: '20px', boxSizing: 'border-box' }}>
                <Stack sx={{ height: '100%' }}>
                    <Typography style={{ display: 'flex', alignItems: 'center', marginBottom: '15px' }}><CircleIcon sx={{ width: '10px', height: '10px', color: '#3A3D50', mr: '10px' }}/>The table you clicked on</Typography>
                    <Typography style={{ display: 'flex', alignItems: 'center' }}><CircleIcon sx={{ width: '10px', height: '10px', color: '#2E6F6B', mr: '10px' }}/>The table you are currently sitting at</Typography>
                </Stack>
            </Box> */}
        </ThemeProvider>
    );
};

export default RoomInformation;
